import React, { useEffect, useState, useMemo } from 'react';
import Login from './Login';
import Chat from './Chat';
import DocumentChat from './DocumentChat';
import AssistantChat from './AssistantChat';
import VectorChat from './VectorChat';
import Usage from './Usage';
import UserManagement from './UserManagement';
import Settings from './Settings';
import PdfAnalysis from './PdfAnalysis';
import PdfTocAnalysis from './PdfTocAnalysis';

function App() {
  const [token, setToken] = useState(localStorage.getItem('token'));
  const [message, setMessage] = useState('Loading...');
  const [environment, setEnvironment] = useState('');
  const [version, setVersion] = useState('');
  const [view, setView] = useState('chat'); // 'chat', 'document-chat', 'assistant-chat', 'usage', 'users', 'pdf' or 'pdf-toc'
  const [chatId] = useState(() => {
    const saved = localStorage.getItem('chatId');
    if (saved) return saved;
    const newId = Math.random().toString(36).substring(7);
    localStorage.setItem('chatId', newId);
    return newId;
  });
  const [documentChatId] = useState(() => {
    const saved = localStorage.getItem('documentChatId');
    if (saved) return saved;
    const newId = 'doc_' + Math.random().toString(36).substring(7);
    localStorage.setItem('documentChatId', newId);
    return newId;
  });
  const [assistantChatId] = useState(() => {
    const saved = localStorage.getItem('assistantChatId');
    if (saved) return saved;
    const newId = 'ast_' + Math.random().toString(36).substring(7);
    localStorage.setItem('assistantChatId', newId);
    return newId;
  });
  const [vectorChatId, setVectorChatId] = useState(() => {
    const saved = localStorage.getItem('vectorChatId');
    if (saved) return saved;
    const newId = 'vec_' + Math.random().toString(36).substring(7);
    localStorage.setItem('vectorChatId', newId);
    return newId;
  });

  const handleVectorChatIdChange = (newId) => {
    localStorage.setItem('vectorChatId', newId);
    setVectorChatId(newId);
  };

  const isAdmin = useMemo(() => {
    if (!token) return false;
    try {
      const payload = JSON.parse(atob(token.split('.')[1]));
      return payload.roles && payload.roles.includes('ROLE_ADMIN');
    } catch (e) {
      return false;
    }
  }, [token]);

  useEffect(() => {
    if (token) {
      fetch('/api/hello', {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      })
        .then(response => {
          if (response.status === 401 || response.status === 403) {
            handleLogout();
            throw new Error('Unauthorized');
          }
          return response.json();
        })
        .then(data => {
          setMessage(data.message);
          setEnvironment(data.environment);
          setVersion(data.version);
        })
        .catch(error => {
          console.error('Error fetching message:', error);
          setMessage('Error fetching message');
        });
    }
  }, [token]);

  const handleLogin = (newToken) => {
    localStorage.setItem('token', newToken);
    setToken(newToken);
  };

  const handleLogout = () => {
    localStorage.removeItem('token');
    setToken(null);
  };

  if (!token) {
    return <Login onLogin={handleLogin} />;
  }

  return (
    <div className="container">
      <div className="row justify-content-center mt-5">
        <div className="col-12 text-center">
          <header className="App-header">
            <h1 className="mb-4">DevelopIT AI Service</h1>
            <div className="card shadow-sm">
              <div className="card-body">
                <p className="card-text"><strong>Message from Backend:</strong> {message}</p>
                <p className="card-text"><strong>Environment:</strong> {environment} | <strong>Version:</strong> {version}</p>
                <div className="mb-3">
                  <div className="d-flex flex-wrap justify-content-center mb-2">
                    <button onClick={() => setView('chat')} className={`btn btn-sm me-2 mb-2 ${view === 'chat' ? 'btn-primary' : 'btn-outline-primary'}`}>Chat</button>
                    <button onClick={() => setView('document-chat')} className={`btn btn-sm me-2 mb-2 ${view === 'document-chat' ? 'btn-primary' : 'btn-outline-primary'}`}>Document Chat</button>
                    <button onClick={() => setView('assistant-chat')} className={`btn btn-sm me-2 mb-2 ${view === 'assistant-chat' ? 'btn-primary' : 'btn-outline-primary'}`}>Assistant Chat</button>
                    <button onClick={() => setView('vector-chat')} className={`btn btn-sm me-2 mb-2 ${view === 'vector-chat' ? 'btn-primary' : 'btn-outline-primary'}`}>Vector Chat</button>
                    <button onClick={() => setView('pdf')} className={`btn btn-sm me-2 mb-2 ${view === 'pdf' ? 'btn-primary' : 'btn-outline-primary'}`}>Analyze PDF</button>
                    <button onClick={() => setView('pdf-toc')} className={`btn btn-sm me-2 mb-2 ${view === 'pdf-toc' ? 'btn-primary' : 'btn-outline-primary'}`}>Analyze ToC</button>
                  </div>
                  <hr className="my-2" />
                  <div className="d-flex flex-wrap justify-content-center mt-2">
                    <button onClick={() => setView('usage')} className={`btn btn-sm me-2 ${view === 'usage' ? 'btn-primary' : 'btn-outline-primary'}`}>Usage</button>
                    {isAdmin && <button onClick={() => setView('users')} className={`btn btn-sm me-2 ${view === 'users' ? 'btn-primary' : 'btn-outline-primary'}`}>Users</button>}
                    {isAdmin && <button onClick={() => setView('settings')} className={`btn btn-sm me-2 ${view === 'settings' ? 'btn-primary' : 'btn-outline-primary'}`}>Settings</button>}
                    <a href="/swagger-ui.html" target="_blank" rel="noopener noreferrer" className="btn btn-outline-info btn-sm me-2">API Docs</a>
                    <button onClick={handleLogout} className="btn btn-outline-danger btn-sm">Logout</button>
                  </div>
                </div>
              </div>
            </div>
            
            {view === 'chat' && <Chat token={token} chatId={chatId} />}
            {view === 'document-chat' && <DocumentChat token={token} chatId={documentChatId} />}
            {view === 'assistant-chat' && <AssistantChat token={token} chatId={assistantChatId} />}
            {view === 'vector-chat' && <VectorChat token={token} chatId={vectorChatId} onChatIdChange={handleVectorChatIdChange} environment={environment} />}
            {view === 'pdf' && <PdfAnalysis token={token} />}
            {view === 'pdf-toc' && <PdfTocAnalysis token={token} />}
            {view === 'usage' && <Usage token={token} />}
            {view === 'users' && isAdmin && <UserManagement token={token} />}
            {view === 'settings' && isAdmin && <Settings token={token} />}
          </header>
        </div>
      </div>
    </div>
  );
}

export default App;
